import { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Tag, Button, Modal, Form, Input, InputNumber, Select, Switch, Space,
  message, Popconfirm, Descriptions, Alert, List, Empty, Radio,
} from 'antd';
import { PlusOutlined, ExperimentOutlined, DeleteOutlined, EditOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { strategyTemplates } from '../../../services/api';

// 场景标签颜色
const SCENE_COLORS = {
  '削峰填谷': 'blue',
  '需量控制': 'orange',
  '防逆流': 'red',
  '新能源消纳': 'green',
  '自定义': 'default',
};
const SCENES = Object.keys(SCENE_COLORS);
const SLOT_LABELS = { peak: '峰段', flat: '平段', valley: '谷段' };
const METRIC_OPTIONS = [
  { value: 'soc', label: '储能SOC(%)' },
  { value: 'grid_demand_kw', label: '园区需量(kW)' },
  { value: 'reverse_power_kw', label: '并网点反送功率(kW)' },
  { value: 'pv_power_kw', label: '光伏出力(kW)' },
  { value: 'load_power_kw', label: '负荷功率(kW)' },
];
const ACTION_OPTIONS = [
  { value: 'storage_discharge', label: '储能放电' },
  { value: 'storage_charge', label: '储能充电' },
  { value: 'storage_standby', label: '储能待机' },
  { value: 'limit_load', label: '负荷压限' },
  { value: 'curtail_pv', label: '光伏限发' },
  { value: 'notify', label: '通知提醒' },
];
const NEEDS_POWER = ['storage_discharge', 'storage_charge', 'limit_load', 'curtail_pv'];

// 条件/动作摘要（列表页展示用）
function condSummary(c) {
  if (c.type === 'time_slot') return `时段∈${(c.slots || []).map((s) => SLOT_LABELS[s] || s).join('/')}`;
  if (c.type === 'threshold') {
    const m = METRIC_OPTIONS.find((x) => x.value === c.metric);
    return `${m ? m.label : c.metric} ${c.operator} ${c.value}`;
  }
  if (c.type === 'event') return '需求响应事件执行中';
  return c.type;
}
function actSummary(a) {
  const o = ACTION_OPTIONS.find((x) => x.value === a.type);
  const label = o ? o.label : a.type;
  if (a.type === 'notify') return `${label}`;
  return a.power_kw != null ? `${label} ${a.power_kw}kW` : label;
}

export default function StrategyPage() {
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  const [evaluation, setEvaluation] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null=新建
  const [simOpen, setSimOpen] = useState(false);
  const [simResult, setSimResult] = useState(null);
  const [simLoading, setSimLoading] = useState(false);
  const [form] = Form.useForm();
  const [simForm] = Form.useForm();
  const user = JSON.parse(localStorage.getItem('qhy_user') || '{}');
  const isAdmin = user.role === 'admin';

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [tpls, ev] = await Promise.all([
        strategyTemplates.list(),
        strategyTemplates.evaluation().catch(() => null),
      ]);
      setList(tpls.data || []);
      setEvaluation(ev ? ev.data : null);
    } catch (err) {
      message.error(err.message || '加载策略模板失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const openCreate = () => {
    setEditing(null);
    form.setFieldsValue({
      name: '', scene: '自定义', priority: 50, target_type: '储能', description: '',
      condition_logic: 'all',
      conditions: [{ type: 'time_slot', slots: ['peak'] }],
      actions: [{ type: 'storage_discharge', power_kw: 200 }],
    });
    setEditOpen(true);
  };

  const openEdit = (tpl) => {
    setEditing(tpl);
    form.setFieldsValue({
      name: tpl.name,
      scene: tpl.scene,
      priority: tpl.priority,
      target_type: tpl.target_type,
      description: tpl.description,
      condition_logic: tpl.rules?.condition_logic || 'all',
      conditions: tpl.rules?.conditions || [],
      actions: tpl.rules?.actions || [],
    });
    setEditOpen(true);
  };

  const saveTemplate = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        name: values.name,
        scene: values.scene,
        priority: values.priority,
        target_type: values.target_type,
        description: values.description,
        rules: {
          condition_logic: values.condition_logic,
          conditions: values.conditions,
          actions: values.actions,
        },
      };
      if (editing) {
        await strategyTemplates.update(editing.id, payload);
        message.success('模板已更新');
      } else {
        await strategyTemplates.create(payload);
        message.success('模板已创建');
      }
      setEditOpen(false);
      loadData();
    } catch (err) {
      if (err.message) message.error(err.message);
    }
  };

  const toggleTemplate = async (tpl, checked) => {
    try {
      await strategyTemplates.toggle(tpl.id, { is_active: checked });
      message.success(`「${tpl.name}」已${checked ? '启用' : '停用'}`);
      loadData();
    } catch (err) {
      message.error(err.message || '操作失败');
    }
  };

  const removeTemplate = async (tpl) => {
    try {
      await strategyTemplates.remove(tpl.id);
      message.success('模板已删除');
      loadData();
    } catch (err) {
      message.error(err.message || '删除失败');
    }
  };

  const runSimulate = async () => {
    try {
      const values = await simForm.validateFields();
      setSimLoading(true);
      const res = await strategyTemplates.simulate(values);
      setSimResult(res.data);
    } catch (err) {
      if (err.message) message.error(err.message);
    } finally {
      setSimLoading(false);
    }
  };

  const columns = [
    { title: '模板名称', dataIndex: 'name', render: (v, r) => <span>{v}{r.is_preset && <Tag style={{ marginLeft: 6 }}>预置</Tag>}</span> },
    { title: '场景', dataIndex: 'scene', render: (v) => <Tag color={SCENE_COLORS[v]}>{v}</Tag> },
    { title: '优先级', dataIndex: 'priority', sorter: (a, b) => a.priority - b.priority, render: (v) => <b>{v}</b> },
    { title: '适用对象', dataIndex: 'target_type' },
    {
      title: '触发条件', key: 'conditions',
      render: (_, r) => (
        <span style={{ fontSize: 12 }}>
          {(r.rules?.conditions || []).map((c, i) => <Tag key={i} style={{ marginBottom: 2 }}>{condSummary(c)}</Tag>)}
          {(r.rules?.conditions || []).length > 1 && (
            <Tag color="purple">{r.rules?.condition_logic === 'any' ? '任一满足' : '全部满足'}</Tag>
          )}
        </span>
      ),
    },
    {
      title: '动作', key: 'actions',
      render: (_, r) => (
        <span style={{ fontSize: 12 }}>
          {(r.rules?.actions || []).map((a, i) => <Tag key={i} color="geekblue">{actSummary(a)}</Tag>)}
        </span>
      ),
    },
    {
      title: '启停', dataIndex: 'is_active',
      render: (v, r) => (
        <Switch
          checked={v} checkedChildren="启用" unCheckedChildren="停用"
          disabled={!isAdmin}
          onChange={(checked) => toggleTemplate(r, checked)}
        />
      ),
    },
    {
      title: '操作', key: 'op',
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} disabled={!isAdmin} onClick={() => openEdit(r)}>编辑</Button>
          <Popconfirm title={`确认删除「${r.name}」？`} onConfirm={() => removeTemplate(r)} disabled={!isAdmin}>
            <Button size="small" danger icon={<DeleteOutlined />} disabled={!isAdmin}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* 当前策略评估状态 */}
      <Card
        title={<span><ThunderboltOutlined /> 当前策略评估</span>} size="small" style={{ marginBottom: 16 }}
        extra={<Button onClick={loadData}>刷新</Button>}
      >
        {evaluation ? (
          <>
            <Descriptions column={{ xs: 1, md: 3 }} size="small">
              <Descriptions.Item label="当前应执行动作">
                <b style={{ color: evaluation.winner ? '#fa8c16' : '#52c41a' }}>{evaluation.action_summary}</b>
              </Descriptions.Item>
              <Descriptions.Item label="胜出策略">
                {evaluation.winner
                  ? <span>{evaluation.winner.name}（优先级 {evaluation.winner.priority}）</span>
                  : '无触发'}
              </Descriptions.Item>
              <Descriptions.Item label="启用模板 / 触发">
                {evaluation.enabled_count} / {evaluation.triggered_count}
              </Descriptions.Item>
              <Descriptions.Item label="工况">
                时段 {SLOT_LABELS[evaluation.context.time_slot]}（{evaluation.context.hour}:00）
                ，SOC {evaluation.context.soc != null ? `${evaluation.context.soc}%` : '无遥测'}
                ，需量 {evaluation.context.grid_demand_kw != null ? `${evaluation.context.grid_demand_kw}kW` : '无遥测'}
                ，反送 {evaluation.context.reverse_power_kw}kW
              </Descriptions.Item>
              <Descriptions.Item label="评估时间" span={2}>
                {new Date(evaluation.evaluated_at).toLocaleString('zh-CN', { hour12: false })}
              </Descriptions.Item>
            </Descriptions>
            <Alert type="info" showIcon style={{ marginTop: 8 }} message={evaluation.control_note} />
          </>
        ) : <Empty description="暂无评估数据" />}
      </Card>

      {/* 模板列表 */}
      <Card
        title="策略模板库" size="small"
        extra={(
          <Space>
            <Button icon={<ExperimentOutlined />} onClick={() => { setSimResult(null); setSimOpen(true); }}>模拟校验</Button>
            {isAdmin && <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>新建模板</Button>}
          </Space>
        )}
      >
        <Table
          rowKey="id" size="small" loading={loading}
          columns={columns} dataSource={list}
          pagination={false}
        />
        <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>
          优先级数值越大越优先，行业惯例：保安全/防逆流 &gt; 保SOC/需量 &gt; 保生产 &gt; 优化收益（削峰填谷套利）。
          预置模板参数为通用经验值，投运前应按园区合同需量、储能额定功率与当地分时电价调整。
        </div>
      </Card>

      {/* 新建/编辑模板 */}
      <Modal
        title={editing ? `编辑模板「${editing.name}」` : '新建策略模板'}
        open={editOpen} width={760}
        onCancel={() => setEditOpen(false)} onOk={saveTemplate} okText="保存"
      >
        <Form form={form} layout="vertical">
          <Space size="middle" style={{ display: 'flex' }} align="start">
            <Form.Item name="name" label="模板名称" rules={[{ required: true, message: '请输入模板名称' }]} style={{ width: 220 }}>
              <Input placeholder="如：午间消纳充电" />
            </Form.Item>
            <Form.Item name="scene" label="场景类型" rules={[{ required: true }]} style={{ width: 140 }}>
              <Select options={SCENES.map((s) => ({ value: s, label: s }))} />
            </Form.Item>
            <Form.Item name="priority" label="优先级（越大越优先）" rules={[{ required: true, message: '请输入优先级' }]} style={{ width: 160 }}>
              <InputNumber min={0} max={100} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="target_type" label="适用对象" style={{ width: 120 }}>
              <Select options={['储能', '光伏', '充电桩', '可调负荷', '全站'].map((s) => ({ value: s, label: s }))} />
            </Form.Item>
          </Space>
          <Form.Item name="description" label="说明">
            <Input.TextArea rows={2} placeholder="策略用途、参数口径说明" />
          </Form.Item>

          <Form.Item name="condition_logic" label="条件组合逻辑">
            <Radio.Group>
              <Radio.Button value="all">全部满足（AND）</Radio.Button>
              <Radio.Button value="any">任一满足（OR）</Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Form.List name="conditions">
            {(fields, { add, remove }) => (
              <Card size="small" title="触发条件" style={{ marginBottom: 16 }}
                extra={<Button size="small" onClick={() => add({ type: 'threshold', metric: 'soc', operator: '>', value: 20 })}>添加条件</Button>}
              >
                {fields.map((field) => (
                  <Space key={field.key} align="start" style={{ display: 'flex', marginBottom: 8 }}>
                    <Form.Item name={[field.name, 'type']} noStyle rules={[{ required: true }]}>
                      <Select
                        style={{ width: 120 }}
                        options={[
                          { value: 'time_slot', label: '分时时段' },
                          { value: 'threshold', label: '阈值比较' },
                          { value: 'event', label: '事件' },
                        ]}
                      />
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate>
                      {() => {
                        const type = form.getFieldValue(['conditions', field.name, 'type']);
                        if (type === 'time_slot') {
                          return (
                            <Form.Item name={[field.name, 'slots']} noStyle rules={[{ required: true, message: '请选择时段' }]}>
                              <Select mode="multiple" style={{ width: 260 }} placeholder="选择时段"
                                options={Object.entries(SLOT_LABELS).map(([v, l]) => ({ value: v, label: l }))} />
                            </Form.Item>
                          );
                        }
                        if (type === 'threshold') {
                          return (
                            <>
                              <Form.Item name={[field.name, 'metric']} noStyle rules={[{ required: true }]}>
                                <Select style={{ width: 180 }} options={METRIC_OPTIONS} />
                              </Form.Item>
                              <Form.Item name={[field.name, 'operator']} noStyle rules={[{ required: true }]}>
                                <Select style={{ width: 70 }} options={['>', '>=', '<', '<=', '=='].map((o) => ({ value: o, label: o }))} />
                              </Form.Item>
                              <Form.Item name={[field.name, 'value']} noStyle rules={[{ required: true, message: '阈值' }]}>
                                <InputNumber style={{ width: 110 }} placeholder="阈值" />
                              </Form.Item>
                            </>
                          );
                        }
                        return (
                          <Form.Item name={[field.name, 'event']} noStyle initialValue="dr_event_active">
                            <Select style={{ width: 220 }} options={[{ value: 'dr_event_active', label: '需求响应事件执行中' }]} />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                    <Button size="small" danger onClick={() => remove(field.name)} disabled={fields.length <= 1}>删除</Button>
                  </Space>
                ))}
              </Card>
            )}
          </Form.List>

          <Form.List name="actions">
            {(fields, { add, remove }) => (
              <Card size="small" title="动作"
                extra={<Button size="small" onClick={() => add({ type: 'storage_discharge', power_kw: 200 })}>添加动作</Button>}
              >
                {fields.map((field) => (
                  <Space key={field.key} align="start" style={{ display: 'flex', marginBottom: 8 }}>
                    <Form.Item name={[field.name, 'type']} noStyle rules={[{ required: true }]}>
                      <Select style={{ width: 140 }} options={ACTION_OPTIONS} />
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate>
                      {() => {
                        const type = form.getFieldValue(['actions', field.name, 'type']);
                        if (NEEDS_POWER.includes(type)) {
                          return (
                            <Form.Item name={[field.name, 'power_kw']} noStyle rules={[{ required: true, message: '功率' }]}>
                              <InputNumber min={1} style={{ width: 140 }} placeholder="功率(kW)" />
                            </Form.Item>
                          );
                        }
                        if (type === 'notify') {
                          return (
                            <Form.Item name={[field.name, 'message']} noStyle>
                              <Input style={{ width: 260 }} placeholder="通知内容（可选）" />
                            </Form.Item>
                          );
                        }
                        return null;
                      }}
                    </Form.Item>
                    <Button size="small" danger onClick={() => remove(field.name)} disabled={fields.length <= 1}>删除</Button>
                  </Space>
                ))}
              </Card>
            )}
          </Form.List>
        </Form>
      </Modal>

      {/* 模拟校验 */}
      <Modal
        title="策略模拟校验（试运行规则，不下发设备）"
        open={simOpen} width={820}
        onCancel={() => setSimOpen(false)}
        footer={<Button type="primary" loading={simLoading} onClick={runSimulate}>运行校验</Button>}
      >
        <Form form={simForm} layout="inline" style={{ marginBottom: 16 }}>
          <Form.Item name="hour" label="小时(0-23)">
            <InputNumber min={0} max={23} placeholder="缺省=当前" />
          </Form.Item>
          <Form.Item name="soc" label="SOC(%)">
            <InputNumber min={0} max={100} />
          </Form.Item>
          <Form.Item name="grid_demand_kw" label="园区需量(kW)">
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item name="reverse_power_kw" label="反送功率(kW)">
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item name="pv_power_kw" label="光伏出力(kW)">
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item name="template_id" label="仅校验模板">
            <Select allowClear style={{ width: 180 }} placeholder="缺省=全部启用模板"
              options={list.map((t) => ({ value: t.id, label: t.name }))} />
          </Form.Item>
        </Form>

        {simResult && (
          <>
            <Alert
              style={{ marginBottom: 12 }}
              type={simResult.winner ? 'warning' : 'success'} showIcon
              message={`模拟结果：${simResult.action_summary}`}
              description={simResult.winner
                ? `胜出策略：${simResult.winner.name}（优先级 ${simResult.winner.priority}）；${simResult.control_note}`
                : simResult.control_note}
            />
            <List
              size="small" bordered
              dataSource={simResult.results}
              renderItem={(r) => (
                <List.Item>
                  <Space direction="vertical" size={2} style={{ width: '100%' }}>
                    <Space>
                      <Tag color={r.triggered ? 'red' : 'default'}>{r.triggered ? '触发' : '未触发'}</Tag>
                      <b>{r.name}</b>
                      <Tag color={SCENE_COLORS[r.scene]}>{r.scene}</Tag>
                      <span style={{ color: '#999' }}>优先级 {r.priority}</span>
                      {r.triggered && <b style={{ color: '#fa8c16' }}>{r.action_summary}</b>}
                    </Space>
                    <span style={{ fontSize: 12, color: '#999' }}>
                      {(r.condition_results || []).map((c) => c.detail).join('；')}
                    </span>
                  </Space>
                </List.Item>
              )}
            />
          </>
        )}
      </Modal>
    </div>
  );
}
